import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  API_URL_agent,
  API_URL_department,
  API_URL_helpTopic,
  API_URL_slaplan,
  API_URL_ticketPriority,
} from "constants/api";
import { helpTopicReducers } from "redux/helpTopicSlice";
import { getData, postData, putData } from "actions";
import { slaplanReducers } from "redux/slaplanSlice";
import { agentReducers } from "redux/agentSlice";
import { departmentReducers } from "redux/departmentSlice";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { AuthContext } from "context/AuthContext";

const FormHelptopics = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const {
    getSlaplanResult,
    getAgentResult,
    getDepartmentResult,
    getTicketPriorityResult,
  } = useSelector((state) => ({
    getHelpTopicResult: state.helpTopic.getHelpTopicResult,
    getSlaplanResult: state.slaplan.getSlaplanResult,
    getAgentResult: state.agent.getAgentResult,
    getDepartmentResult: state.department.getDepartmentResult,
    getTicketPriorityResult: state.ticketPriority.getTicketPriorityResult,
  }));

  const helpTopicFetch = useCallback(
    async (params) => {
      getData(
        API_URL_helpTopic,
        params,
        { dispatch, redux: helpTopicReducers },
        "GET_HELPTOPIC"
      );
    },
    [dispatch]
  );

  const slaplanFetch = useCallback(
    async (params) => {
      getData(
        API_URL_slaplan,
        params,
        { dispatch, redux: slaplanReducers },
        "GET_SLAPLAN"
      );
    },
    [dispatch]
  );

  const agentFetch = useCallback(
    async (params) => {
      getData(
        API_URL_agent,
        params,
        { dispatch, redux: agentReducers },
        "GET_AGENT"
      );
    },
    [dispatch]
  );

  const departmentFetch = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );

  const ticketPriorityFetch = useCallback(
    async (params) => {
      getData(
        API_URL_ticketPriority,
        params,
        { dispatch, redux: ticketPriorityReducers },
        "GET_TICKETPRIORITY"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      topic: location.state ? location.state.item.topic : "",
      parent_topic: location.state ? location.state.item.parent_topic : "",
      status: location.state ? location.state.item.status : 0,
      type: location.state ? location.state.item.type : 0,
      department: location.state ? location.state.item.department : "",
      priority: location.state ? location.state.item.priority : "",
      sla_plan: location.state ? location.state.item.sla_plan : "",
      auto_assign: location.state ? location.state.item.auto_assign : "",
      internal_notes: location.state ? location.state.item.internal_notes : "",
      isdefault: location.state ? location.state.item.isdefault : 0,
      custom_form: location.state ? location.state.item.custom_form : "",
    },
    validationSchema: Yup.object({
      topic: Yup.string().required("Name is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_helpTopic,
          formData,
          { dispatch, redux: helpTopicReducers },
          "ADD_HELPTOPIC",
          () => {
            navigate("/administrator/manage/helptopics");
          }
        );
      } else {
        putData(
          `${API_URL_helpTopic}${location.state.item.id}/`,
          formData,
          { dispatch, redux: helpTopicReducers },
          "ADD_HELPTOPIC",
          () => {
            navigate("/administrator/manage/helptopics");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    const params = `?limit=${9999999}&all=1`;
    const paramsAgent = `?limit=${9999999}&role_group=!user&is_active=1`;
    helpTopicFetch(params);
    slaplanFetch(params);
    agentFetch(paramsAgent);
    departmentFetch(params);
    ticketPriorityFetch(params);
  }, []);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Help topic"}
        excludedPaths={["manage", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <form onSubmit={handleFormSubmit}>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
            {typeForm === "create" ? "Create" : "Edit"}
          </div>
          <div className="h-auto w-auto px-4 pb-6 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Topic <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="topic"
                    id="topic"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.topic}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.topic && formik.errors.topic && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.topic}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Status
                </label>
                <div className="flex flex-wrap">
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="status1"
                      type="radio"
                      checked={formik.values.status === 1}
                      onChange={() => formik.setFieldValue("status", 1)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2 mr-5">Active</span>
                  </label>
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="status0"
                      type="radio"
                      checked={formik.values.status === 0}
                      onChange={() => formik.setFieldValue("status", 0)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2">Inactive</span>
                  </label>
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Type
                </label>
                <div className="flex flex-wrap">
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="type1"
                      type="radio"
                      checked={formik.values.type === 1}
                      onChange={() => formik.setFieldValue("type", 1)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2 mr-5">Public</span>
                  </label>
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="type0"
                      type="radio"
                      checked={formik.values.type === 0}
                      onChange={() => formik.setFieldValue("type", 0)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2">Private</span>
                  </label>
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-3 md:col-start-1 lg:col-span-3 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Department
                </label>
                <div>
                  <select
                    id="department"
                    name="department"
                    value={formik.values.department}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a department</option>
                    <optgroup label="departments">
                      {getDepartmentResult.results &&
                        getDepartmentResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-3 md:col-start-4 lg:col-span-3 lg:col-start-4">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Priority <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="priority"
                    name="priority"
                    value={formik.values.priority}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a priority</option>
                    <optgroup label="Priorities">
                      {getTicketPriorityResult.results &&
                        getTicketPriorityResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.priority}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-3 md:col-start-7 lg:col-span-3 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  SLA Plan
                </label>
                <div>
                  <select
                    id="sla_plan"
                    name="sla_plan"
                    value={formik.values.sla_plan}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a SLA plan</option>
                    <optgroup label="SLA plans">
                      {getSlaplanResult.results &&
                        getSlaplanResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-3 md:col-start-10 lg:col-span-3 lg:col-start-10">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Auto assign
                </label>
                <div>
                  <select
                    id="auto_assign"
                    name="auto_assign"
                    value={formik.values.auto_assign}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select an agent</option>
                    <optgroup label="agents">
                      {getAgentResult.results &&
                        getAgentResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.full_name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                Internal notes
              </label>
              <div>
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.internal_notes}
                  onChange={(e) =>
                    formik.setFieldValue("internal_notes", e.editor.getData())
                  }
                />
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormHelptopics;
